import { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useApp } from "../AppProvider"
import { API_URL } from "../constants"

/**
 * ! Deprecated: Use tanstack/react-query for more responsive data fetching and mutations
 * See usePatient.ts, etc. for examples
 */
export const useApi = () => {
  const { token, setUser, setToken, addToast } = useApp()
  const navigate = useNavigate()

  const makeApiRequest = useCallback(
    async (path: string, method = "GET", data?: any, additionalHeaders = {}) => {
      const headers: any = {
        "Content-Type": "application/json",
        ...additionalHeaders,
      }
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      const result = await fetch(`${API_URL}${path}`, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
      })

      if (result.status >= 200 && result.status < 300) {
        return result.json()
      } else if (result.status === 401) {
        setUser(undefined)
        setToken(undefined)
        navigate("/login")
      } else if (result.status === 403) {
        addToast("Unauthorized", "You are not authorized to access that resource", "error")
      } else {
        const data = await result.json()
        throw data
      }
    },
    [token],
  )

  const api = useMemo(
    () => ({
      me: () => makeApiRequest("/me"),
      login: (email: string, password: string) =>
        makeApiRequest("/authentication", "POST", { email, password }),

      // Users - Org
      getUsersForOrg: (orgId: string, offset: any, items: any) =>
        makeApiRequest(`/organizations/${orgId}/users?${new URLSearchParams({ offset, items }).toString()}`),
      getUserForOrg: (orgId: string, userId: string) =>
        makeApiRequest(`/organizations/${orgId}/users/${userId}`),
      createUserForOrg: (orgId: string, data: any) =>
        makeApiRequest(`/organizations/${orgId}/users`, "POST", data),
      updateUserForOrg: (orgId: string, userId: string, data: any) =>
        makeApiRequest(`/organizations/${orgId}/users/${userId}`, "PATCH", data),
      deleteUserForOrg: (orgId: string, userId: string) =>
        makeApiRequest(`/organizations/${orgId}/users/${userId}`, "DELETE"),

      // Users - Private
      getUsers: (offset: any, items: any) =>
        makeApiRequest(`/private/users?${new URLSearchParams({ offset, items }).toString()}`),
      getUser: (userId: string) => makeApiRequest(`/private/users/${userId}`),
      createUser: (data: any) => makeApiRequest(`/private/users`, "POST", data),
      updateUser: (userId: string, data: any) => makeApiRequest(`/private/users/${userId}`, "PATCH", data),
      deleteUser: (userId: string) => makeApiRequest(`/private/users/${userId}`, "DELETE"),

      // Organizations
      getOrganization: (organizationId: string) => makeApiRequest(`/private/organizations/${organizationId}`),
      getOrganizations: (offset: any, items: any) =>
        makeApiRequest(`/private/organizations?${new URLSearchParams({ offset, items }).toString()}`),
      createOrganization: (data: any) => makeApiRequest("/private/organizations", "POST", data),
      updateOrganization: (organizationId: string, data: any) =>
        makeApiRequest(`/private/organizations/${organizationId}`, "PATCH", data),
      deleteOrganization: (organizationId: string) => {
        return makeApiRequest(`/private/organizations/${organizationId}`, "DELETE")
      },

      // Providers
      getProvider: (organizationId: string, providerId: string) =>
        makeApiRequest(`/organizations/${organizationId}/providers/${providerId}`),
      createProvider: (organizationId: string, data: any) =>
        makeApiRequest(`/organizations/${organizationId}/providers`, "POST", data),

      // Patients
      getPatients: (organizationId: string) => makeApiRequest(`/organizations/${organizationId}/patients`),
      getPatientByClientId: (organizationId: string, clientId: string) =>
        makeApiRequest(`/organizations/${organizationId}/patients/${clientId}`),
      getPatient: (organizationId: string, patientId: string) =>
        makeApiRequest(`/organizations/${organizationId}/patients/patient/${patientId}`),
      createPatient: (organizationId: string, data: { clientId: string; name: string; metaData?: any }) =>
        makeApiRequest(`/organizations/${organizationId}/patients`, "POST", data),

      // Lab Reports
      getLabReports: (organizationId: string) =>
        makeApiRequest(`/organizations/${organizationId}/lab-reports`),
      getLabReport: (organizationId: string, reportId: string) =>
        makeApiRequest(`/organizations/${organizationId}/lab-reports/${reportId}`),

      // Test Configs
      getTestConfigs: (organizationId: string) =>
        makeApiRequest(`/organizations/${organizationId}/test-configs`),
      createTestConfig: (organizationId: string, data: any) =>
        makeApiRequest(`/organizations/${organizationId}/test-configs`, "POST", data),
      updateTestConfig: (organizationId: string, configId: string, data: any) =>
        makeApiRequest(`/organizations/${organizationId}/test-configs/${configId}`, "PATCH", data),
      deleteTestConfig: (organizationId: string, configId: string) =>
        makeApiRequest(`/organizations/${organizationId}/test-configs/${configId}`, "DELETE"),

      // Email
      sendEmail: (organizationId: string, data: any) =>
        makeApiRequest(`/organizations/${organizationId}/email`, "POST", data),
    }),
    [makeApiRequest],
  )

  return api
}
